import { ComponentType, lazy, LazyExoticComponent } from 'react';

type ComponentImport<T extends ComponentType<any>> = () => Promise<{ default: T }>;

// Silent retries before we resort to a hard reload.
const MAX_RETRIES = 2;
// Base delay between retries, in ms. Backs off (doubles) on each attempt.
const RETRY_DELAY_MS = 350;
// sessionStorage guard against a reload loop when a chunk is genuinely gone.
export const CHUNK_RELOAD_FLAG = 'maverick:chunk-reloaded';

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Detects the webpack/Vite "failed to load a code-split chunk" family of errors.
 * These surface as ChunkLoadError but the name/message varies across bundlers.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) { return false; }
  let name = '';
  let message = '';
  if (typeof error === 'string') {
    message = error;
  } else if (typeof error === 'object') {
    ({ name = '', message = '' } = error as { name?: string; message?: string });
  }
  return (
    name === 'ChunkLoadError' ||
    /Loading( CSS)? chunk .* failed|error loading dynamically imported module/i.test(message)
  );
};

// sessionStorage can throw (SecurityError) when storage is blocked — sandboxed
// iframes, strict privacy modes. Guard every access so this resilience layer can
// never itself crash a route.
const readReloadFlag = (): boolean => {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === 'true';
  } catch {
    return false;
  }
};

const markReloaded = (): boolean => {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, 'true');
    return true;
  } catch {
    return false;
  }
};

/**
 * Resolves a dynamic import, retrying transient failures a few times. If it still
 * fails with a ChunkLoadError, reload once per tab session to fetch the current
 * index.html and chunk manifest. Further failures reach the router's errorElement
 * so the user can reload manually without an automatic reload loop.
 */
export async function importWithRetry<T extends ComponentType<any>>(
  factory: ComponentImport<T>,
  retries: number = MAX_RETRIES,
  delayMs: number = RETRY_DELAY_MS
): Promise<{ default: T }> {
  try {
    // A successful import does not prove that other chunks on the page recovered.
    // Keep the reload guard for the tab session, including across manual reloads.
    return await factory();
  } catch (error) {
    // Only retry/reload chunk-load failures. A real error thrown while a module
    // evaluates should surface immediately, not after ~1s of pointless backoff.
    if (!isChunkLoadError(error)) {
      throw error;
    }

    if (retries > 0) {
      await wait(delayMs);
      return importWithRetry(factory, retries - 1, delayMs * 2);
    }

    // Retries exhausted. Reload once to pull the fresh bundle — but only if we can
    // persist the guard, otherwise a reload loop is possible. If storage is blocked,
    // skip the reload and let the router errorElement offer a manual reload instead.
    if (!readReloadFlag() && markReloaded()) {
      window.location.reload();
      // Reload tears down the app; return a never-settling promise so Suspense keeps
      // showing the loader instead of flashing the error fallback before navigation.
      return new Promise<{ default: T }>(() => {});
    }

    throw error;
  }
}

/**
 * Drop-in replacement for React.lazy that adds chunk-load resilience. Imported as
 * `lazy` in lazy-load.tsx so every route benefits without per-route changes.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: ComponentImport<T>
): LazyExoticComponent<T> {
  return lazy(() => importWithRetry(factory));
}
